import React, {useState, useEffect} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import Breadcrumbs from '../Components/Breadcrumbs';
import {
  CustomInput,
  CustomSelect,
  CustomToggle,
  CustomTextarea
} from '../Components/CustomFormElements';
import Button from '../Components/Buttons';
import {AlertProvider, useAlert} from '../Components/AlertContext';
import {ConfirmationProvider, useConfirmation} from '../Components/ConfirmationContext';
import PageLoader from '../Components/PageLoader';
import {ConnectionObjectMapApiProvider, useConnectionObjectMapApi} from "../Services/ConnectionObjectMapApiProvider";

function ConnectionObjectMapDetail({canEdit}) {
  const navigate = useNavigate();
  const {organizationId, connectionObjectMapId} = useParams();
  const {showAlert} = useAlert();

  const {
    createConnectionObjectMap,
    updateConnectionObjectMapById,
    getConnectionObjectMapById,
    getConnections,
  } = useConnectionObjectMapApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isNew, setIsNew] = useState(true);
  const [isDisabled, setIsDisabled] = useState(true);

  const [connections, setConnections] = useState([]);

  const [connectionId, setConnectionId] = useState();
  const [isActive, setIsActive] = useState(true);
  const [isRequired, setIsRequired] = useState(false);
  const [defaultValue, setDefaultValue] = useState('');
  const [connectionObjectType, setConnectionObjectType] = useState();
  const [connectionObjectAttribute, setConnectionObjectAttribute] = useState('');
  const [connectionObjectAttributeType, setConnectionObjectAttributeType] = useState();
  const [sourceAttributeType, setSourceAttributeType] = useState();
  const [sourceAttribute, setSourceAttribute] = useState();

  useEffect(() => {
    const func = async () => {
      setLoading(true);
        getConnections().then(connections => {
        setConnections(connections ?? []);
      });
      try {
          console.log('connectionObjectMapId', connectionObjectMapId);
        if (connectionObjectMapId !== 'new-connection-object-map') {
          const response = await getConnectionObjectMapById(connectionObjectMapId);
          console.log('response', response);
          setConnectionId(response.connection_id);
          setIsActive(response.is_active);
          setIsRequired(response.is_required);
          setDefaultValue(response.default_value);
          setConnectionObjectType(response.connection_object_type);
          setConnectionObjectAttribute(response.connection_object_attribute);
          setConnectionObjectAttributeType(response.connection_object_attribute_type);
          setSourceAttributeType(response.source_attribute_type);
          setSourceAttribute(response.source_attribute);
        }
      } catch (error) {
        setError('Error fetching connection object map data');
        console.error('Error fetching connection object map data:', error);
      } finally {
        setLoading(false);
      }
    }
    func();
  }, [connectionObjectMapId]);

  useEffect(() => {
    setIsNew(connectionObjectMapId === 'new-connection-object-map');
    setIsDisabled(!canEdit);
  }, [connectionObjectMapId, canEdit])

  const onSave = async () => {
    const connectionObjectMap = {
      connection_key: connectionId,
      is_active: isActive,
      is_required: isRequired,
      default_value: defaultValue,
      connection_object_type: connectionObjectType,
      connection_object_attribute: connectionObjectAttribute,
      connection_object_attribute_type: connectionObjectAttributeType,
      source_attribute_type: sourceAttributeType,
      source_attribute: sourceAttribute
    };

    setLoading(true);

    try {
      if (isNew) {
        await createConnectionObjectMap(connectionObjectMap);
        showAlert('success', 'Connection object map created successfully');
      } else {
        await updateConnectionObjectMapById(connectionObjectMapId, connectionObjectMap);
        showAlert('success', 'Connection object map updated successfully');
      }
      navigate(`/organizations/${organizationId}/connection-object-map`);
    } catch (error) {
      console.error('Error saving connection object map:', error);
      showAlert('fail', 'Failed to save connection object map');
    } finally {
      setLoading(false);
    }
  };

  const onCancel = () => {
    navigate(`/organizations/${organizationId}/connection-object-map`);
  };

  if (loading) {
    return <PageLoader/>;
  }

  return (
    <div className="container">
      <Breadcrumbs/>
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-6">
          <h2 className='h2-title'>Connection Object Map</h2>
          <div className='gap d-flex flex-row-to-col flex-column'>
            <div className='int-col-3 p-2 border-yes radius-yes full-width-on-sml-scr'>
              <h4 className='h4-title'>Map Settings</h4>

                <div className="input-block d-flex flex-row justify-between">
                  <div className="input-description">
                    <div className="input-title">Connection</div>
                    <div className="small-font">Select connection</div>
                  </div>
                  <div className="align-wrapper d-flex align-center">
                    <CustomSelect
                      id="connection-select"
                      label="Connection"
                      options={(connections || []).map(dc => ({value: dc.id, label: dc.name}))}
                      value={connectionId}
                      multiSelect={false}
                      onChange={val => setConnectionId(val)}
                      disabled={isDisabled}
                    />
                  </div>
                </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Object Type</div>
                  <div className='small-font'>Select object type</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='connection-object-type'
                    label="Object Type"
                    options={[
                      {value: 'lead', label: 'Lead'},
                      {value: 'process', label: 'Process'},
                      {value: 'buyer', label: 'Buyer'}
                    ]}
                    value={connectionObjectType}
                    onChange={(value) => setConnectionObjectType(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Source Attribute Type</div>
                  <div className='small-font'>Select source attribute type</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='source-attribute-type'
                    label="Source Attribute Type"
                    options={[
                      {value: 'variable', label: 'Variable'},
                      {value: 'dynamic', label: 'Dynamic'},
                      {value: 'thread', label: 'Thread'}
                    ]}
                    value={sourceAttributeType}
                    onChange={(value) => setSourceAttributeType(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Source Attribute</div>
                  <div className='small-font'>Select source attribute</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='source-attribute'
                    label="Source Attribute"
                    options={[
                      {value: 'phone', label: 'Phone'},
                      {value: 'email', label: 'Email'},
                      {value: 'name', label: 'Name'}
                    ]}
                    value={sourceAttribute}
                    onChange={(value) => setSourceAttribute(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Object Attribute Type</div>
                  <div className='small-font'>Select object attribute type</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomSelect
                    id='connection-object-attribute-type'
                    label="Object Attribute Type"
                    options={[
                      {value: 'string', label: 'String'},
                      {value: 'integer', label: 'Integer'},
                      {value: 'datetime', label: 'Datetime'},
                      {value: 'boolean', label: 'Boolean'}
                    ]}
                    value={connectionObjectAttributeType}
                    onChange={(value) => setConnectionObjectAttributeType(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Object Attribute</div>
                  <div className='small-font'>Enter object attribute</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomInput
                    id="object-attribute"
                    placeholder="Enter object attribute"
                    value={connectionObjectAttribute}
                    onChange={(id, value) => setConnectionObjectAttribute(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Is Required</div>
                  <div className='small-font'>Is this field required?</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomToggle
                    id="toggle-required"
                    label={isRequired ? 'Required' : 'Optional'}
                    checked={isRequired}
                    onChange={(id, value) => setIsRequired(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Default Value</div>
                  <div className='small-font'>Default value if none provided</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomInput
                    id="default-value"
                    placeholder="Enter default value"
                    value={defaultValue}
                    onChange={(id, value) => setDefaultValue(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>

              <div className='input-block d-flex flex-row justify-between'>
                <div className='input-description'>
                  <div className='input-title'>Activate map</div>
                  <div className='small-font'>Set map to be active or inactive</div>
                </div>
                <div className='align-wrapper d-flex align-center'>
                  <CustomToggle
                    id="toggle-status"
                    label={isActive ? 'Active' : 'Inactive'}
                    checked={isActive}
                    onChange={(id, value) => setIsActive(value)}
                    disabled={isDisabled}
                  />
                </div>
              </div>
            </div>

            <div className='buttons-container d-flex justify-end'>
              <Button variant='primary' size='medium' disabled={isDisabled} onClick={onSave}>
                Save
              </Button>
              <Button variant='danger' size='medium' onClick={onCancel}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function ConnectionObjectMapDetailWrapper({canEdit}) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
          <ConnectionObjectMapApiProvider>
            <ConnectionObjectMapDetail canEdit={canEdit}/>
          </ConnectionObjectMapApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default ConnectionObjectMapDetailWrapper;